import React, { useState, useEffect, useMemo } from 'react';
import {
  XCircle,
  Loader2,
  Building2,
  MapPin,
  FileText,
  CheckCircle,
  Search,
  Upload,
  Eye,
  History,
  AlertTriangle,
  Clock,
  X,
  Package,
  Box,
  Wrench,
  Sparkles,
  Plus,
} from 'lucide-react';
import { consultarCNPJ, validarCNPJ } from '../services/cnpjService';
import { consultarCEP, validarCEP } from '../services/cepService';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { functions, storage } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import AssociateProductsModal from './AssociateProductsModal';

function tipoInsumoNormalizado(doc) {
  const t = doc?.tipo_insumo;
  if (!t || t === 'materia_prima') return 'ingrediente';
  return t;
}

const ABAS_INSUMOS_FORNECEDOR = [
  { id: 'ingredientes', label: 'Ingredientes', tipo: 'ingrediente', icon: Package },
  { id: 'embalagens', label: 'Embalagens', tipo: 'embalagem', icon: Box },
  { id: 'servicos', label: 'Serviço', tipo: 'servico', icon: Wrench },
  {
    id: 'limpeza-conservacao',
    label: 'Limpeza e Conservação',
    tipo: 'limpeza_conservacao',
    icon: Sparkles,
  },
];

const ABAS_INSUMO_IDS = new Set(ABAS_INSUMOS_FORNECEDOR.map((a) => a.id));

const TIPOS_CATALOGO_OPCOES = [
  { value: 'ingrediente', label: 'Ingredientes', icon: Package },
  { value: 'embalagem', label: 'Embalagens', icon: Box },
  { value: 'servico', label: 'Serviços', icon: Wrench },
  { value: 'limpeza_conservacao', label: 'Limpeza e conservação', icon: Sparkles },
];

function normalizarTiposCatalogoForm(raw) {
  if (!Array.isArray(raw)) return [];
  return [
    ...new Set(
      raw
        .map((t) => (t === 'materia_prima' ? 'ingrediente' : t))
        .filter((t) => TIPOS_CATALOGO_OPCOES.some((o) => o.value === t))
    ),
  ];
}

const SupplierModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  onSubmitWithoutInvite, // ✅ NOVO: Handler para cadastrar sem enviar convite
  mode = 'create', // 'create', 'edit', 'invite' ou 'view'
  initialData = {},
  loading = false,
  error = '',
  success = ''
}) => {
  const { getEmpresaId, hasPermission } = useAuth() || {};
  const isViewMode = mode === 'view';
  const [formData, setFormData] = useState({
    nome_empresa: initialData.nome_empresa || '',
    cnpj: initialData.cnpj || '',
    email: initialData.email || '',
    telefone: initialData.telefone || '',
    nome_contato: initialData.nome_contato || '',
    tipos_catalogo_insumo: normalizarTiposCatalogoForm(initialData.tipos_catalogo_insumo),
    observacoes: initialData.observacoes || '',
    descricao_servicos: initialData.descricao_servicos || '',
    capacidade_producao: initialData.capacidade_producao || '',
    regioes_atendimento: initialData.regioes_atendimento || [],
    certificacoes: initialData.certificacoes || [],
    endereco: {
      logradouro: initialData.endereco?.logradouro || '',
      numero: initialData.endereco?.numero || '',
      complemento: initialData.endereco?.complemento || '',
      bairro: initialData.endereco?.bairro || '',
      cidade: initialData.endereco?.cidade || '',
      estado: initialData.endereco?.estado || '',
      cep: initialData.endereco?.cep || ''
    }
  });

  const [errors, setErrors] = useState({});

  // Sincroniza dados ao abrir edição/visualização (inclui tipos_catalogo_insumo)
  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      nome_empresa: initialData.nome_empresa || '',
      cnpj: initialData.cnpj || '',
      email: initialData.email || '',
      telefone: initialData.telefone || '',
      nome_contato: initialData.nome_contato || '',
      tipos_catalogo_insumo: normalizarTiposCatalogoForm(initialData.tipos_catalogo_insumo),
      observacoes: initialData.observacoes || '',
      descricao_servicos: initialData.descricao_servicos || '',
      capacidade_producao: initialData.capacidade_producao || '',
      regioes_atendimento: initialData.regioes_atendimento || [],
      certificacoes: initialData.certificacoes || [],
      endereco: {
        logradouro: initialData.endereco?.logradouro || '',
        numero: initialData.endereco?.numero || '',
        complemento: initialData.endereco?.complemento || '',
        bairro: initialData.endereco?.bairro || '',
        cidade: initialData.endereco?.cidade || '',
        estado: initialData.endereco?.estado || '',
        cep: initialData.endereco?.cep || ''
      }
    });
    setErrors({});
  }, [isOpen, mode, initialData.id, initialData.supplier_id, initialData.uid]);

  // Estados para consulta de CEP
  const [consultandoCEP, setConsultandoCEP] = useState(false);
  const [erroCEP, setErroCEP] = useState('');

  // ✅ NOVOS ESTADOS: Consulta de CNPJ
  const [consultandoCNPJ, setConsultandoCNPJ] = useState(false);
  const [erroCNPJ, setErroCNPJ] = useState('');

  // ✅ NOVOS ESTADOS: Aba de Documentos
  const [activeTab, setActiveTab] = useState('informacoes'); // 'informacoes' ou 'documentos'
  const [documentos, setDocumentos] = useState([]); // Documentos já enviados
  const [tiposDocumentos, setTiposDocumentos] = useState([]); // Tipos de documentos obrigatórios disponíveis
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);
  const [loadingTipos, setLoadingTipos] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showNovoDocumentoModal, setShowNovoDocumentoModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [arquivo, setArquivo] = useState(null);
  const [errorDocumentos, setErrorDocumentos] = useState('');
  const [successDocumentos, setSuccessDocumentos] = useState('');

  // ✅ Estados: abas de insumos (ingredientes, embalagens, serviço, limpeza)
  const [produtosFornecedor, setProdutosFornecedor] = useState([]);
  const [loadingProdutosFornecedor, setLoadingProdutosFornecedor] = useState(false);
  const [showAssociateModal, setShowAssociateModal] = useState(false);

  const fornecedorId =
    initialData.id || initialData.supplier_id || initialData.uid || null;

  const empresaIdCliente = useMemo(() => {
    const emp =
      initialData.id_empresa && String(initialData.id_empresa).trim() !== 'default'
        ? String(initialData.id_empresa).trim()
        : null;
    return emp || (typeof getEmpresaId === 'function' ? getEmpresaId() : null);
  }, [initialData.id_empresa, getEmpresaId]);

  const abaInsumoAtiva = ABAS_INSUMOS_FORNECEDOR.find((a) => a.id === activeTab);
  const abasInsumoVisiveis = useMemo(() => {
    const tipos = normalizarTiposCatalogoForm(
      formData.tipos_catalogo_insumo?.length
        ? formData.tipos_catalogo_insumo
        : initialData.tipos_catalogo_insumo
    );
    return ABAS_INSUMOS_FORNECEDOR.filter((aba) => tipos.includes(aba.tipo));
  }, [formData.tipos_catalogo_insumo, initialData.tipos_catalogo_insumo]);

  useEffect(() => {
    if (!ABAS_INSUMO_IDS.has(activeTab)) return;
    if (!abasInsumoVisiveis.some((aba) => aba.id === activeTab)) {
      setActiveTab('informacoes');
    }
  }, [activeTab, abasInsumoVisiveis]);

  const produtosFiltradosAba = useMemo(() => {
    if (!abaInsumoAtiva) return [];
    return produtosFornecedor.filter(
      (p) => tipoInsumoNormalizado(p) === abaInsumoAtiva.tipo
    );
  }, [produtosFornecedor, abaInsumoAtiva]);

  // Estados brasileiros
  const BRAZILIAN_STATES = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  // Certificações disponíveis
  const CERTIFICACOES = [
    'orgânico',
    'fair_trade',
    'iso_9001',
    'iso_14001',
    'haccp',
    'brc',
    'ifs',
    'fscc_22000',
    'kosher',
    'halal',
    'vegano',
    'sem_gluten',
    'sem_lactose'
  ];

  // Funções de carregamento de classificação removidas - agora gerenciado via MPs

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar erro do campo
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const toggleTipoCatalogo = (tipo) => {
    if (isViewMode || loading) return;
    setFormData((prev) => {
      const atuais = Array.isArray(prev.tipos_catalogo_insumo) ? prev.tipos_catalogo_insumo : [];
      const next = atuais.includes(tipo)
        ? atuais.filter((t) => t !== tipo)
        : [...atuais, tipo];
      return { ...prev, tipos_catalogo_insumo: next };
    });
    if (errors.tipos_catalogo_insumo) {
      setErrors((prev) => ({ ...prev, tipos_catalogo_insumo: '' }));
    }
  };

  const handleAddressChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      endereco: {
        ...prev.endereco,
        [field]: value
      }
    }));
    
    // Limpar erro do campo
    if (errors[`endereco.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`endereco.${field}`]: ''
      }));
    }
  };

  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/^(\d{5})(\d{3})$/, '$1-$2');
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    } else {
      return numbers.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    }
  };

  // ✅ NOVA FUNÇÃO: Consultar CNPJ na Receita Federal
  const handleConsultarCNPJ = async () => {
    const cnpjLimpo = formData.cnpj.replace(/\D/g, '');
    
    if (cnpjLimpo.length !== 14) {
      setErroCNPJ('CNPJ deve ter 14 dígitos');
      return;
    }

    if (!validarCNPJ(cnpjLimpo)) {
      setErroCNPJ('CNPJ inválido');
      return;
    }

    try {
      setConsultandoCNPJ(true);
      setErroCNPJ('');

      console.log('🔍 Consultando CNPJ do fornecedor:', cnpjLimpo);
      
      const resultado = await consultarCNPJ(cnpjLimpo);

      if (resultado.success) {
        console.log('✅ Dados do CNPJ recebidos:', resultado.data);
        
        // Preencher campos com os dados recebidos
        setFormData(prev => {
          // ✅ CORREÇÃO: Formatar CEP de "xx.xxx-xxx" para "xxxxx-xxx"
          let cepFormatado = resultado.data.endereco?.cep || prev.endereco.cep;
          if (cepFormatado) {
            // Remover pontos e traços, depois reformatar
            const cepLimpo = cepFormatado.replace(/\D/g, '');
            if (cepLimpo.length === 8) {
              cepFormatado = cepLimpo.replace(/^(\d{5})(\d{3})$/, '$1-$2');
            }
          }
          
          return {
            ...prev,
            nome_empresa: resultado.data.nome_empresa || prev.nome_empresa,
            telefone: resultado.data.telefone || prev.telefone,
            endereco: {
              ...prev.endereco,
              logradouro: resultado.data.endereco?.logradouro || prev.endereco.logradouro,
              numero: resultado.data.endereco?.numero || prev.endereco.numero,
              complemento: resultado.data.endereco?.complemento || prev.endereco.complemento,
              bairro: resultado.data.endereco?.bairro || prev.endereco.bairro,
              cidade: resultado.data.endereco?.cidade || prev.endereco.cidade,
              estado: resultado.data.endereco?.estado || prev.endereco.estado,
              cep: cepFormatado // ✅ Usando CEP formatado corretamente
            }
          };
        });

        // Limpar erros dos campos preenchidos
        setErrors(prev => {
          const newErrors = { ...prev };
          if (resultado.data.nome_empresa) delete newErrors.nome_empresa;
          if (resultado.data.telefone) delete newErrors.telefone;
          if (resultado.data.endereco?.logradouro) delete newErrors['endereco.logradouro'];
          if (resultado.data.endereco?.numero) delete newErrors['endereco.numero'];
          if (resultado.data.endereco?.bairro) delete newErrors['endereco.bairro'];
          if (resultado.data.endereco?.cidade) delete newErrors['endereco.cidade'];
          if (resultado.data.endereco?.estado) delete newErrors['endereco.estado'];
          if (resultado.data.endereco?.cep) delete newErrors['endereco.cep'];
          return newErrors;
        });

      } else {
        setErroCNPJ(resultado.error || 'Erro ao consultar CNPJ');
      }
    } catch (error) {
      console.error('❌ Erro ao consultar CNPJ:', error);
      setErroCNPJ('Erro ao consultar CNPJ. Tente novamente.');
    } finally {
      setConsultandoCNPJ(false);
    }
  };

  // Função para consultar CEP na API ViaCEP
  const handleConsultarCEP = async () => {
    const cepLimpo = formData.endereco.cep.replace(/\D/g, '');
    
    // Valida se o CEP tem 8 dígitos
    if (cepLimpo.length !== 8) {
      setErroCEP('CEP deve ter 8 dígitos');
      return;
    }

    // Valida se o CEP é válido
    if (!validarCEP(formData.endereco.cep)) {
      setErroCEP('CEP inválido');
      return;
    }

    setConsultandoCEP(true);
    setErroCEP('');

    try {
      const resultado = await consultarCEP(formData.endereco.cep);
      
      if (resultado.success) {
        // Preenche os campos com os dados da API
        setFormData(prev => ({
          ...prev,
          endereco: {
            ...prev.endereco,
            logradouro: resultado.data.logradouro,
            bairro: resultado.data.bairro,
            cidade: resultado.data.cidade,
            estado: resultado.data.estado,
            complemento: resultado.data.complemento || prev.endereco.complemento
          }
        }));

        // Limpa erros de validação dos campos preenchidos
        setErrors(prev => {
          const newErrors = { ...prev };
          if (resultado.data.logradouro) delete newErrors['endereco.logradouro'];
          if (resultado.data.bairro) delete newErrors['endereco.bairro'];
          if (resultado.data.cidade) delete newErrors['endereco.cidade'];
          if (resultado.data.estado) delete newErrors['endereco.estado'];
          return newErrors;
        });

        console.log('✅ Dados do CEP preenchidos automaticamente');
      } else {
        setErroCEP(resultado.error || 'Erro ao consultar CEP');
      }
    } catch (error) {
      console.error('❌ Erro ao consultar CEP:', error);
      setErroCEP('Erro ao consultar CEP. Tente novamente.');
    } finally {
      setConsultandoCEP(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nome_empresa.trim()) {
      newErrors.nome_empresa = 'Nome da empresa é obrigatório';
    }

    if (!formData.cnpj.trim()) {
      newErrors.cnpj = 'CNPJ é obrigatório';
    } else if (formData.cnpj.replace(/\D/g, '').length !== 14) {
      newErrors.cnpj = 'CNPJ deve ter 14 dígitos';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.telefone.trim()) {
      newErrors.telefone = 'Telefone é obrigatório';
    }

    if (!formData.nome_contato.trim()) {
      newErrors.nome_contato = 'Nome do contato é obrigatório';
    }

    const tiposSelecionados = normalizarTiposCatalogoForm(formData.tipos_catalogo_insumo);
    if (mode === 'create' || mode === 'edit') {
      if (tiposSelecionados.length === 0) {
        newErrors.tipos_catalogo_insumo = 'Selecione ao menos um tipo de fornecedor';
      }
    }

    // Validações de endereço
    if (!formData.endereco.logradouro.trim()) {
      newErrors['endereco.logradouro'] = 'Logradouro é obrigatório';
    }

    if (!formData.endereco.numero.trim()) {
      newErrors['endereco.numero'] = 'Número é obrigatório';
    }

    if (!formData.endereco.bairro.trim()) {
      newErrors['endereco.bairro'] = 'Bairro é obrigatório';
    }

    if (!formData.endereco.cidade.trim()) {
      newErrors['endereco.cidade'] = 'Cidade é obrigatória';
    }

    if (!formData.endereco.estado) {
      newErrors['endereco.estado'] = 'Estado é obrigatório';
    }

    if (!formData.endereco.cep.trim()) {
      newErrors['endereco.cep'] = 'CEP é obrigatório';
    } else if (!/^\d{5}-\d{3}$/.test(formData.endereco.cep)) {
      newErrors['endereco.cep'] = 'CEP deve estar no formato XXXXX-XXX';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const payload = {
      ...formData,
      tipos_catalogo_insumo: normalizarTiposCatalogoForm(formData.tipos_catalogo_insumo),
    };
    console.log('🔍 SupplierModal: formData antes de enviar:', JSON.stringify(payload, null, 2));
    console.log('🔍 SupplierModal: Campos no formData:', Object.keys(payload));
    
    onSubmit(payload);
  };

  // ✅ NOVO: Carregar documentos JÁ ENVIADOS do fornecedor
  const loadDocumentos = async () => {
    // Extrair ID do fornecedor de diferentes possíveis campos
    const fornecedorId = initialData.id || initialData.supplier_id || initialData.uid;
    
    if (!fornecedorId || mode === 'create') {
      console.log('⚠️ SupplierModal: Sem ID do fornecedor ou modo create. initialData:', initialData);
      return;
    }
    
    try {
      setLoadingDocumentos(true);
      setErrorDocumentos('');
      
      console.log('🔍 SupplierModal: Carregando documentos enviados para fornecedor:', fornecedorId);
      
      const listDocumentos = httpsCallable(functions, 'listDocumentosEnviados');
      const result = await listDocumentos({ fornecedor_id: fornecedorId });
      
      if (result.data.success) {
        setDocumentos(result.data.data.documentos || []);
        console.log('✅ SupplierModal: Documentos enviados carregados:', result.data.data.documentos?.length);
      }
    } catch (err) {
      console.error('❌ Erro ao carregar documentos:', err);
      setErrorDocumentos(err.message || 'Erro ao carregar documentos.');
    } finally {
      setLoadingDocumentos(false);
    }
  };

  // ✅ NOVO: Carregar tipos de documentos obrigatórios disponíveis
  const loadTiposDocumentos = async () => {
    try {
      setLoadingTipos(true);
      
      // Buscar documentos obrigatórios ativos (da empresa + globais)
      const getTipos = httpsCallable(functions, 'getDocumentTypesOptions');
      const result = await getTipos();
      
      if (result.data.success) {
        setTiposDocumentos(result.data.data || []);
        console.log('✅ SupplierModal: Tipos de documentos carregados:', result.data.data?.length);
      }
    } catch (err) {
      console.error('❌ Erro ao carregar tipos de documentos:', err);
      setErrorDocumentos(err.message || 'Erro ao carregar tipos de documentos.');
    } finally {
      setLoadingTipos(false);
    }
  };

  // ✅ NOVO: Carregar documentos quando a aba for aberta ou quando o modal abrir em modo view/edit
  useEffect(() => {
    const fornecedorId = initialData.id || initialData.supplier_id || initialData.uid;
    console.log('🔍 SupplierModal useEffect - activeTab:', activeTab, 'mode:', mode, 'fornecedorId:', fornecedorId, 'initialData:', initialData);
    if (activeTab === 'documentos' && (mode === 'view' || mode === 'edit') && fornecedorId) {
      console.log('✅ SupplierModal: Carregando documentos...');
      loadDocumentos();
    }
  }, [activeTab, mode, initialData]);

  const loadProdutosFornecedor = async () => {
    if (!fornecedorId) return;
    if (!empresaIdCliente || empresaIdCliente === 'default') {
      setProdutosFornecedor([]);
      return;
    }
    try {
      setLoadingProdutosFornecedor(true);
      const fn = httpsCallable(functions, 'listProdutosRecebimentoPorFornecedor');
      const result = await fn({
        fornecedor_id: fornecedorId,
        empresa_id: empresaIdCliente,
        limit: 1500,
      });
      if (result.data?.success) {
        const produtos = result.data.data.products || result.data.data.items || [];
        setProdutosFornecedor(produtos);
      } else {
        setProdutosFornecedor([]);
      }
    } catch (err) {
      console.error('❌ SupplierModal: Erro ao carregar insumos do fornecedor:', err);
      setProdutosFornecedor([]);
    } finally {
      setLoadingProdutosFornecedor(false);
    }
  };

  useEffect(() => {
    if (
      ABAS_INSUMO_IDS.has(activeTab) &&
      (mode === 'view' || mode === 'edit') &&
      fornecedorId
    ) {
      loadProdutosFornecedor();
    }
  }, [activeTab, mode, fornecedorId, empresaIdCliente]);

  // ✅ NOVO: Abrir modal para selecionar tipo de documento
  const handleOpenNovoDocumentoModal = async () => {
    setShowNovoDocumentoModal(true);
    setErrorDocumentos('');
    setSuccessDocumentos('');
    await loadTiposDocumentos();
  };

  // ✅ NOVO: Fechar modal de novo documento
  const handleCloseNovoDocumentoModal = () => {
    setShowNovoDocumentoModal(false);
    setSelectedDoc(null);
    setErrorDocumentos('');
    setSuccessDocumentos('');
  };

  // ✅ NOVO: Selecionar tipo de documento e abrir modal de upload
  const handleSelecionarTipoDocumento = (tipoDoc) => {
    setSelectedDoc(tipoDoc);
    setShowNovoDocumentoModal(false);
    setShowUploadModal(true);
    setArquivo(null);
    setErrorDocumentos('');
    setSuccessDocumentos('');
  };

  // ✅ NOVO: Abrir modal de upload (para reenvio)
  const handleOpenUploadModal = (doc) => {
    setSelectedDoc(doc);
    setShowUploadModal(true);
    setArquivo(null);
    setErrorDocumentos('');
    setSuccessDocumentos('');
  };

  // ✅ NOVO: Fechar modal de upload
  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setSelectedDoc(null);
    setArquivo(null);
    setErrorDocumentos('');
    setSuccessDocumentos('');
  };

  // ✅ NOVO: Selecionar arquivo
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar formato
    const extensao = file.name.split('.').pop()?.toUpperCase();
    if (!selectedDoc.formato_aceito.includes(extensao)) {
      setErrorDocumentos(`Formato não aceito. Formatos permitidos: ${selectedDoc.formato_aceito.join(', ')}`);
      return;
    }

    // Validar tamanho
    const tamanhoMB = file.size / (1024 * 1024);
    const limiteMB = selectedDoc.tamanho_maximo_mb ?? 50;
    if (tamanhoMB > limiteMB) {
      setErrorDocumentos(`Arquivo muito grande. Tamanho máximo: ${limiteMB}MB`);
      return;
    }

    setArquivo(file);
    setErrorDocumentos('');
  };

  // ✅ NOVO: Fazer upload do documento
  const handleUpload = async () => {
    const fornecedorId = initialData.id || initialData.supplier_id || initialData.uid;
    
    if (!arquivo || !fornecedorId) {
      setErrorDocumentos('Selecione um arquivo para enviar.');
      return;
    }

    try {
      setUploading(true);
      setErrorDocumentos('');

      // Upload para Storage
      const storageRef = ref(storage, `documentos/${fornecedorId}/${selectedDoc.id}/${Date.now()}_${arquivo.name}`);
      const uploadResult = await uploadBytes(storageRef, arquivo);
      const downloadURL = await getDownloadURL(uploadResult.ref);

      // Obter id_empresa do fornecedor
      const fornecedorData = initialData;
      const idEmpresa = fornecedorData.id_empresa;
      
      if (!idEmpresa) {
        setErrorDocumentos('Não foi possível identificar a empresa do fornecedor');
        return;
      }

      // Registrar no Firestore usando createDocument
      const createDoc = httpsCallable(functions, 'createDocument');
      const result = await createDoc({
        nome: arquivo.name,
        tipo: selectedDoc.nome, // Nome do documento obrigatório (ex: "Licença Sanitária")
        descricao: `Upload de ${selectedDoc.nome}`,
        url_arquivo: downloadURL,
        tamanho_arquivo: arquivo.size,
        tipo_mime: arquivo.type,
        id_empresa: idEmpresa,
        id_fornecedor: fornecedorId
      });

      if (result.data.success) {
        setSuccessDocumentos('Documento enviado com sucesso!');
        setTimeout(() => {
          handleCloseUploadModal();
          loadDocumentos();
        }, 2000);
      } else {
        setErrorDocumentos(result.data.message || 'Erro ao enviar documento.');
      }
    } catch (err) {
      console.error('❌ Erro ao fazer upload:', err);
      setErrorDocumentos(err.message || 'Erro ao enviar documento. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  // ✅ NOVO: Obter badge de status
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pendente':
        return { bg: 'bg-gray-100', text: 'text-gray-800', label: '📋 Pendente', icon: Clock };
      case 'em_analise':
        return { bg: 'bg-blue-100', text: 'text-blue-800', label: '🔄 Em Análise', icon: Clock };
      case 'aprovado':
        return { bg: 'bg-green-100', text: 'text-green-800', label: '✅ Aprovado', icon: CheckCircle };
      case 'rejeitado':
        return { bg: 'bg-red-100', text: 'text-red-800', label: '❌ Recusado', icon: XCircle };
      case 'vencido':
        return { bg: 'bg-red-100', text: 'text-red-800', label: '🔴 Vencido', icon: AlertTriangle };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-800', label: status, icon: FileText };
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <Building2 className="h-5 w-5 mr-2" />
              {mode === 'create' ? 'Novo Fornecedor' : 
               mode === 'view' ? 'Detalhes do Fornecedor' : 
               'Cadastro de Fornecedor'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-red-500" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-700" />
              <span className="text-green-700">{success}</span>
            </div>
          )}

          {/* ✅ NOVO: Sistema de Abas (apenas para view/edit) */}
          {(mode === 'view' || mode === 'edit') && (initialData.id || initialData.supplier_id || initialData.uid) && (
            <div className="mb-6 border-b border-gray-200">
              <nav className="flex flex-wrap gap-x-4 gap-y-1 -mb-px">
                <button
                  type="button"
                  onClick={() => setActiveTab('informacoes')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'informacoes'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Informações
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('documentos')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'documentos'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Documentos
                </button>
                {abasInsumoVisiveis.map((aba) => {
                  const Icon = aba.icon;
                  return (
                    <button
                      key={aba.id}
                      type="button"
                      onClick={() => setActiveTab(aba.id)}
                      className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap inline-flex items-center gap-1.5 ${
                        activeTab === aba.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {aba.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          )}

          {/* ✅ NOVO: Conteúdo da Aba Documentos */}
          {activeTab === 'documentos' && (mode === 'view' || mode === 'edit') && (initialData.id || initialData.supplier_id || initialData.uid) && (
            <div className="space-y-4">
              {/* Botão Novo Documento */}
              {mode === 'edit' && (
                <div className="flex justify-end mb-4">
                  <button
                    onClick={handleOpenNovoDocumentoModal}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Upload className="h-4 w-4" />
                    Novo Documento
                  </button>
                </div>
              )}

              {errorDocumentos && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <span className="text-red-700">{errorDocumentos}</span>
                </div>
              )}

              {successDocumentos && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-green-700">{successDocumentos}</span>
                </div>
              )}

              {loadingDocumentos ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                  <span className="ml-2 text-gray-600">Carregando documentos...</span>
                </div>
              ) : documentos.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">Nenhum documento enviado ainda.</p>
                  {mode === 'edit' && (
                    <button
                      onClick={handleOpenNovoDocumentoModal}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Upload className="h-4 w-4" />
                      Enviar Primeiro Documento
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {documentos.map(doc => {
                    const badge = getStatusBadge(doc.status || 'pendente');
                    const StatusIcon = badge.icon;
                    
                    // Converter timestamp do Firestore para Date se necessário
                    const dataUpload = doc.data_upload?.toDate ? doc.data_upload.toDate() : (doc.data_upload ? new Date(doc.data_upload) : null);
                    const dataAprovacao = doc.data_aprovacao?.toDate ? doc.data_aprovacao.toDate() : (doc.data_aprovacao ? new Date(doc.data_aprovacao) : null);
                    
                    return (
                      <div key={doc.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="text-lg font-semibold text-gray-900">{doc.nome || 'Documento sem nome'}</h4>
                              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                                <StatusIcon className="h-3 w-3" />
                                {badge.label}
                              </span>
                            </div>
                            {doc.tipo && (
                              <p className="text-sm text-gray-600 mb-2">Tipo: {doc.tipo}</p>
                            )}
                            {doc.descricao && (
                              <p className="text-sm text-gray-500 mb-2">{doc.descricao}</p>
                            )}
                            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                              {dataUpload && (
                                <span>📅 Enviado em: {dataUpload.toLocaleString('pt-BR')}</span>
                              )}
                              {dataAprovacao && (
                                <span>✅ Aprovado em: {dataAprovacao.toLocaleString('pt-BR')}</span>
                              )}
                              {doc.tamanho_arquivo && (
                                <span>📦 Tamanho: {(doc.tamanho_arquivo / 1024).toFixed(2)} KB</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {doc.url_arquivo && (
                          <div className="flex gap-2">
                            <a
                              href={doc.url_arquivo}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                            >
                              <Eye className="h-4 w-4" />
                              Visualizar Documento
                            </a>
                            <a
                              href={doc.url_arquivo}
                              download
                              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                            >
                              <Upload className="h-4 w-4" />
                              Download
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Abas de insumos por tipo */}
          {abaInsumoAtiva &&
            (mode === 'view' || mode === 'edit') &&
            fornecedorId && (
            <div className="space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <abaInsumoAtiva.icon className="h-4 w-4" />
                  {abaInsumoAtiva.label} associados ({produtosFiltradosAba.length})
                </h4>
                {mode === 'edit' && hasPermission('manage_suppliers') && (
                  <button
                    type="button"
                    onClick={() => setShowAssociateModal(true)}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4" />
                    Associar {abaInsumoAtiva.label.toLowerCase()}
                  </button>
                )}
              </div>
              {loadingProdutosFornecedor ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                  <span className="ml-2 text-gray-600">
                    Carregando {abaInsumoAtiva.label.toLowerCase()}…
                  </span>
                </div>
              ) : produtosFiltradosAba.length === 0 ? (
                <div className="rounded-lg bg-gray-50 p-8 text-center">
                  <abaInsumoAtiva.icon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                  <p className="text-gray-600">
                    Nenhum insumo de {abaInsumoAtiva.label.toLowerCase()} associado a este
                    fornecedor.
                  </p>
                  {mode === 'edit' && hasPermission('manage_suppliers') && (
                    <button
                      type="button"
                      onClick={() => setShowAssociateModal(true)}
                      className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4" />
                      Associar agora
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid max-h-80 grid-cols-1 gap-3 overflow-y-auto md:grid-cols-2 lg:grid-cols-3">
                  {produtosFiltradosAba.map((product) => (
                    <div
                      key={product.id}
                      className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 break-words">
                          {product.nome || product.nome_produto}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Código: {product.codigo || product.codigo_produto || product.codigo_origem || '—'}
                        </div>
                        {(product.parametros_qualidade || product.parametros)?.length > 0 && (
                          <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            {(product.parametros_qualidade || product.parametros || []).length} parâmetro(s)
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ✅ NOVO: Modal para Selecionar Tipo de Documento */}
          {showNovoDocumentoModal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Selecionar Tipo de Documento</h3>
                    <p className="text-sm text-gray-600 mt-1">Escolha o tipo de documento que deseja enviar</p>
                  </div>
                  <button onClick={handleCloseNovoDocumentoModal} className="text-gray-400 hover:text-gray-600">
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {loadingTipos ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                    <span className="ml-2 text-gray-600">Carregando tipos de documentos...</span>
                  </div>
                ) : tiposDocumentos.length === 0 ? (
                  <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Nenhum tipo de documento disponível.</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {tiposDocumentos.map(tipo => (
                      <button
                        key={tipo.id}
                        onClick={() => handleSelecionarTipoDocumento(tipo)}
                        className="w-full text-left border border-gray-200 rounded-lg p-4 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="text-base font-semibold text-gray-900 mb-1">{tipo.nome}</h4>
                            {tipo.descricao && (
                              <p className="text-sm text-gray-600 mb-2">{tipo.descricao}</p>
                            )}
                            <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                              <span>📑 {tipo.tipo}</span>
                              <span>🔄 {tipo.frequencia}</span>
                              {tipo.validade_dias && <span>⏱️ {tipo.validade_dias} dias</span>}
                              <span>📄 {tipo.formato_aceito?.join(', ') || 'N/A'}</span>
                            </div>
                          </div>
                          <Upload className="h-5 w-5 text-gray-400" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                <div className="flex justify-end mt-6">
                  <button
                    onClick={handleCloseNovoDocumentoModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ✅ NOVO: Modal de Upload de Documento */}
          {showUploadModal && selectedDoc && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
              <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Enviar Documento</h3>
                    <p className="text-sm text-gray-600 mt-1">{selectedDoc.nome}</p>
                  </div>
                  <button onClick={handleCloseUploadModal} className="text-gray-400 hover:text-gray-600">
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {errorDocumentos && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    <span className="text-sm text-red-700">{errorDocumentos}</span>
                  </div>
                )}

                {successDocumentos && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-green-700">{successDocumentos}</span>
                  </div>
                )}

                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Requisitos:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>📄 Formatos aceitos: {selectedDoc.formato_aceito.join(', ')}</li>
                    <li>📦 Tamanho máximo: {selectedDoc.tamanho_maximo_mb ?? 50}MB</li>
                    {selectedDoc.validade_dias && (
                      <li>⏱️ Validade: {selectedDoc.validade_dias} dias após aprovação</li>
                    )}
                  </ul>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selecionar Arquivo *
                  </label>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    accept={selectedDoc.formato_aceito.map(f => `.${f.toLowerCase()}`).join(',')}
                  />
                  {arquivo && (
                    <p className="text-sm text-green-600 mt-2 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      {arquivo.name} ({(arquivo.size / 1024).toFixed(0)} KB)
                    </p>
                  )}
                </div>

                {selectedDoc.instrucoes_upload && (
                  <div className="bg-gray-50 p-3 rounded-lg mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-1">Instruções:</p>
                    <p className="text-sm text-gray-600">{selectedDoc.instrucoes_upload}</p>
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <button
                    onClick={handleCloseUploadModal}
                    disabled={uploading}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={!arquivo || uploading}
                    className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" />
                        Enviar
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {mode === 'create' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                Cadastrar Fornecedor Convidado
              </h4>
              <p className="text-sm text-blue-700">
                Preencha os dados básicos do fornecedor. Um convite será enviado por email para que ele complete o cadastro.
              </p>
            </div>
          )}

          {/* ✅ NOVO: Mostrar formulário apenas na aba Informações ou se não houver abas */}
          {(activeTab === 'informacoes' || (mode === 'create' || !initialData.id)) && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <fieldset disabled={isViewMode} className={isViewMode ? 'opacity-75' : ''}>
            {/* Campo CNPJ - Primeiro campo do formulário */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CNPJ *
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.cnpj}
                  onChange={(e) => {
                    const formatted = formatCNPJ(e.target.value);
                    if (formatted.replace(/\D/g, '').length <= 14) {
                      handleInputChange('cnpj', formatted);
                      setErroCNPJ(''); // Limpa erro ao digitar
                    }
                  }}
                  className={`flex-1 border rounded-md px-3 py-2 ${
                    errors.cnpj ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="00.000.000/0000-00"
                  maxLength={18}
                />
                <button
                  type="button"
                  onClick={handleConsultarCNPJ}
                  disabled={consultandoCNPJ || loading || formData.cnpj.replace(/\D/g, '').length !== 14}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {consultandoCNPJ ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Consultando...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4" />
                      Consultar
                    </>
                  )}
                </button>
              </div>
              {errors.cnpj && (
                <p className="text-red-500 text-sm mt-1">{errors.cnpj}</p>
              )}
              {erroCNPJ && (
                <p className="text-red-500 text-sm mt-1">{erroCNPJ}</p>
              )}
            </div>

            {/* Informações Básicas */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Building2 className="h-4 w-4 mr-2" />
                Informações da Empresa
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome da Empresa *
                  </label>
                  <input
                    type="text"
                    value={formData.nome_empresa}
                    onChange={(e) => handleInputChange('nome_empresa', e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.nome_empresa ? 'border-red-300' : 'border-gray-300'
                    } ${isViewMode ? 'bg-gray-50' : ''}`}
                    placeholder="Nome da empresa"
                    disabled={isViewMode || loading}
                  />
                  {errors.nome_empresa && (
                    <p className="text-red-500 text-sm mt-1">{errors.nome_empresa}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Fornecedor *
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Define em quais menus o fornecedor aparece (Ingredientes, Embalagens, etc.).
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {TIPOS_CATALOGO_OPCOES.map(({ value, label, icon: Icon }) => {
                      const checked = (formData.tipos_catalogo_insumo || []).includes(value);
                      return (
                        <label
                          key={value}
                          className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer ${
                            checked
                              ? 'border-blue-500 bg-blue-50 text-blue-900'
                              : 'border-gray-300 bg-white text-gray-700'
                          } ${isViewMode ? 'cursor-default' : 'hover:border-blue-300'}`}
                        >
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            checked={checked}
                            onChange={() => toggleTipoCatalogo(value)}
                            disabled={isViewMode || loading}
                          />
                          <Icon className="h-4 w-4 shrink-0" />
                          <span>{label}</span>
                        </label>
                      );
                    })}
                  </div>
                  {errors.tipos_catalogo_insumo && (
                    <p className="text-red-500 text-sm mt-1">{errors.tipos_catalogo_insumo}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.email ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="contato@empresa.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone *
                  </label>
                  <input
                    type="text"
                    value={formData.telefone}
                    onChange={(e) => handleInputChange('telefone', formatPhone(e.target.value))}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.telefone ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="(11) 99999-9999"
                  />
                  {errors.telefone && (
                    <p className="text-red-500 text-sm mt-1">{errors.telefone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Contato *
                  </label>
                  <input
                    type="text"
                    value={formData.nome_contato}
                    onChange={(e) => handleInputChange('nome_contato', e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors.nome_contato ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Nome completo do contato"
                  />
                  {errors.nome_contato && (
                    <p className="text-red-500 text-sm mt-1">{errors.nome_contato}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Endereço */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <MapPin className="h-4 w-4 mr-2" />
                Endereço
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Logradouro *
                  </label>
                  <input
                    type="text"
                    value={formData.endereco.logradouro}
                    onChange={(e) => handleAddressChange('logradouro', e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors['endereco.logradouro'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Rua, Avenida, etc."
                  />
                  {errors['endereco.logradouro'] && (
                    <p className="text-red-500 text-sm mt-1">{errors['endereco.logradouro']}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número *
                  </label>
                  <input
                    type="text"
                    value={formData.endereco.numero}
                    onChange={(e) => handleAddressChange('numero', e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors['endereco.numero'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="123"
                  />
                  {errors['endereco.numero'] && (
                    <p className="text-red-500 text-sm mt-1">{errors['endereco.numero']}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Complemento
                  </label>
                  <input
                    type="text"
                    value={formData.endereco.complemento}
                    onChange={(e) => handleAddressChange('complemento', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Apto, Sala, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bairro *
                  </label>
                  <input
                    type="text"
                    value={formData.endereco.bairro}
                    onChange={(e) => handleAddressChange('bairro', e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors['endereco.bairro'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Nome do bairro"
                  />
                  {errors['endereco.bairro'] && (
                    <p className="text-red-500 text-sm mt-1">{errors['endereco.bairro']}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cidade *
                  </label>
                  <input
                    type="text"
                    value={formData.endereco.cidade}
                    onChange={(e) => handleAddressChange('cidade', e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors['endereco.cidade'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Nome da cidade"
                  />
                  {errors['endereco.cidade'] && (
                    <p className="text-red-500 text-sm mt-1">{errors['endereco.cidade']}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado *
                  </label>
                  <select
                    value={formData.endereco.estado}
                    onChange={(e) => handleAddressChange('estado', e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 ${
                      errors['endereco.estado'] ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Selecione o estado</option>
                    {BRAZILIAN_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                  {errors['endereco.estado'] && (
                    <p className="text-red-500 text-sm mt-1">{errors['endereco.estado']}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CEP *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.endereco.cep}
                      onChange={(e) => {
                        handleAddressChange('cep', formatCEP(e.target.value));
                        setErroCEP(''); // Limpa erro ao digitar
                      }}
                      className={`flex-1 border rounded-md px-3 py-2 ${
                        errors['endereco.cep'] ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="00000-000"
                      maxLength={9}
                    />
                    <button
                      type="button"
                      onClick={handleConsultarCEP}
                      disabled={consultandoCEP || formData.endereco.cep.replace(/\D/g, '').length !== 8}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {consultandoCEP ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Consultando...
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4" />
                          CEP
                        </>
                      )}
                    </button>
                  </div>
                  {errors['endereco.cep'] && (
                    <p className="text-red-500 text-sm mt-1">{errors['endereco.cep']}</p>
                  )}
                  {erroCEP && (
                    <p className="text-red-500 text-sm mt-1">{erroCEP}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Informações Adicionais */}
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <FileText className="h-4 w-4 mr-2" />
                Informações Adicionais
              </h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição dos Serviços
                  </label>
                  <textarea
                    value={formData.descricao_servicos}
                    onChange={(e) => handleInputChange('descricao_servicos', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 h-20"
                    placeholder="Descreva os produtos ou serviços oferecidos..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Capacidade de Produção
                  </label>
                  <input
                    type="text"
                    value={formData.capacidade_producao}
                    onChange={(e) => handleInputChange('capacidade_producao', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Ex: 1000 kg/dia, 500 unidades/mês"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  <textarea
                    value={formData.observacoes}
                    onChange={(e) => handleInputChange('observacoes', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 h-20"
                    placeholder="Observações adicionais sobre o fornecedor..."
                  />
                </div>
              </div>
            </div>

            </fieldset>
            {/* Botões - apenas mostrar se estiver na aba informações ou modo create */}
            {(activeTab === 'informacoes' || mode === 'create' || !initialData.id) && (
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                {isViewMode ? 'Fechar' : 'Cancelar'}
              </button>
              {!isViewMode && mode === 'create' && onSubmitWithoutInvite && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    if (!validateForm()) {
                      return;
                    }
                    onSubmitWithoutInvite({
                      ...formData,
                      tipos_catalogo_insumo: normalizarTiposCatalogoForm(formData.tipos_catalogo_insumo),
                    });
                  }}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Cadastrando...
                    </>
                  ) : (
                    'Cadastrar'
                  )}
                </button>
              )}
              {!isViewMode && (
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      {mode === 'create' ? 'Enviando Convite...' : 'Enviando...'}
                    </>
                  ) : (
                    mode === 'create' ? 'Enviar Convite' : 'Enviar Cadastro'
                  )}
                </button>
              )}
            </div>
            )}
          </form>
          )}
        </div>
      </div>

      <AssociateProductsModal
        isOpen={showAssociateModal}
        onClose={() => {
          setShowAssociateModal(false);
          if (ABAS_INSUMO_IDS.has(activeTab)) {
            loadProdutosFornecedor();
          }
        }}
        supplierId={fornecedorId}
        supplierName={initialData.nome_empresa}
        companyId={empresaIdCliente}
        filtroTipoCatalogoInicial={abaInsumoAtiva?.tipo || 'ingrediente'}
      />
    </div>
  );
};

export default SupplierModal;
